import {
  EditOutlined,
  FormOutlined,
  InboxOutlined,
  ProfileOutlined,
  ShoppingOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { AInput, AModal, ASelect, ASwitch } from '../../../components';
import { useCallback, useEffect, useState } from 'react';
import { Card, Col, Form, Row, Space, Typography } from 'antd';
import { useToast } from '../../../hooks/toast';
import { IProduct, IProductCategory } from '../../../shared/interfaces';
import { schemaProduct } from '../schemas/yup.product.schema';
import { UNITY_LIST } from '../../../shared/enums/unity.enum';
import { patchProduct, postProduct } from '../../../http';
import { useI18n } from '../../../shared/i18n';

const { Text } = Typography;

interface IProductProps {
  open: boolean;
  close: () => void;
  product: IProduct | null;
  setProducts: React.Dispatch<React.SetStateAction<IProduct[]>>;
  categories: IProductCategory[];
}

const ProductCreateEdit: React.FC<IProductProps> = ({
  open,
  close,
  product,
  setProducts,
  categories,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const isEditing = !!product;
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [unity, setUnity] = useState<string>('');
  const [productType, setProductType] = useState<number>(0);
  const [salePrice, setSalePrice] = useState<number>(0);
  const [minimumInventory, setMinimumInventory] = useState<number>(0);
  const [maximumInventory, setMaximumInventory] = useState<number>(0);
  const [status, setStatus] = useState<boolean>(false);

  const productTypeOptions = [
    { label: t('productModal.productTypes.supply'), value: 0 },
    { label: t('productModal.productTypes.retail'), value: 1 },
  ];

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setCategoryId('');
    setUnity('');
    setProductType(0);
    setSalePrice(0);
    setMinimumInventory(0);
    setMaximumInventory(0);
    setStatus(false);
  }, []);

  const handleCreate = useCallback(async () => {
    const payload = {
      name,
      description: description || null,
      product_type: Number(productType),
      sale_price: productType === 1 ? Number(salePrice) : 0,
      minimum_inventory: Number(minimumInventory),
      maximum_inventory: Number(maximumInventory),
      unity: unity || null,
      category_id: categoryId || null,
      status: true,
    };
    schemaProduct
      .validate(payload)
      .then(async () => {
        await postProduct(payload)
          .then((data) => {
            setProducts((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('productModal.toast.successTitle'),
              description: t('productModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('productModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('productModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('productModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    name,
    description,
    productType,
    salePrice,
    unity,
    categoryId,
    minimumInventory,
    maximumInventory,
    setProducts,
    addToast,
    close,
    t,
  ]);

  const handleEdit = useCallback(async () => {
    const payload = {
      name,
      description: description || null,
      product_type: Number(productType),
      sale_price: productType === 1 ? Number(salePrice) : 0,
      minimum_inventory: Number(minimumInventory),
      maximum_inventory: Number(maximumInventory),
      unity: unity || null,
      category_id: categoryId || null,
      status: Boolean(status),
    };
    schemaProduct
      .validate(payload)
      .then(async () => {
        await patchProduct(product?.id, payload)
          .then((data) => {
            setProducts((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('productModal.toast.infoTitle'),
              description: t('productModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('productModal.toast.errorTitle'),
              description:
                error.response.data.error ?? t('productModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('productModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [
    name,
    description,
    productType,
    salePrice,
    minimumInventory,
    maximumInventory,
    unity,
    categoryId,
    status,
    product?.id,
    setProducts,
    addToast,
    close,
    t,
  ]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (product) {
      setName(product.name);
      setDescription(product.description);
      setProductType(Number(product.product_type ?? 0));
      setSalePrice(Number(product.sale_price ?? 0));
      setMinimumInventory(product.minimum_inventory);
      setCategoryId(product.category_id);
      setMaximumInventory(product.maximum_inventory);
      setUnity(product.unity);
      setStatus(!!product.status);
      return;
    }

    resetForm();
  }, [open, product, resetForm]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        isEditing ? (
          <>
            <EditOutlined /> {t('productModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('productModal.titleCreate')}
          </>
        )
      }
      onOk={isEditing ? handleEdit : handleCreate}
      width={680}
    >
      <Form layout="vertical" style={{ maxWidth: '100%', marginTop: 8 }}>
        <Card size="small" style={{ marginBottom: 12 }}>
          <Row gutter={[12, 0]}>
            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ShoppingOutlined style={{ color: '#1890ff' }} />
                    <span>{t('productModal.fields.name')}</span>
                  </Space>
                }
                required
              >
                <AInput
                  placeholder={t('productModal.fields.namePlaceholder')}
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ProfileOutlined style={{ color: '#13c2c2' }} />
                    <span>{t('productModal.fields.description')}</span>
                  </Space>
                }
              >
                <AInput
                  placeholder={t('productModal.fields.descriptionPlaceholder')}
                  name="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={14}>
              <Form.Item
                label={
                  <Space size={6}>
                    <TagsOutlined style={{ color: '#2f54eb' }} />
                    <span>{t('productModal.fields.category')}</span>
                  </Space>
                }
              >
                <ASelect
                  showSearch
                  placeholder={t('productModal.fields.categoryPlaceholder')}
                  value={categoryId}
                  onChange={(e) => setCategoryId(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  options={categories.map((cat) => ({
                    label: cat.description,
                    value: cat.id,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={10}>
              <Form.Item
                label={
                  <Space size={6}>
                    <TagsOutlined style={{ color: '#722ed1' }} />
                    <span>{t('productModal.fields.productType')}</span>
                  </Space>
                }
                required
              >
                <ASelect
                  value={productType}
                  onChange={(value) => setProductType(Number(value))}
                  options={productTypeOptions}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={10}>
              <Form.Item
                label={
                  <Space size={6}>
                    <InboxOutlined style={{ color: '#faad14' }} />
                    <span>{t('productModal.fields.unity')}</span>
                  </Space>
                }
              >
                <ASelect
                  showSearch
                  placeholder={t('productModal.fields.unityPlaceholder')}
                  value={unity}
                  onChange={(e) => setUnity(e)}
                  filterOption={(input, option: any) =>
                    (option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  allowClear
                  options={UNITY_LIST.map((cat) => ({
                    label: cat.label,
                    value: cat.value,
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={14}>
              <Form.Item
                label={
                  <Space size={6}>
                    <ShoppingOutlined style={{ color: '#52c41a' }} />
                    <span>{t('productModal.fields.salePrice')}</span>
                  </Space>
                }
                required={productType === 1}
              >
                <AInput
                  placeholder={
                    productType === 1
                      ? t('productModal.fields.salePricePlaceholder')
                      : t('productModal.fields.salePriceDisabledPlaceholder')
                  }
                  name="sale_price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={salePrice}
                  disabled={productType !== 1}
                  onChange={(e) => setSalePrice(+e.target.value)}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          size="small"
          title={t('productModal.sections.inventory')}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('productModal.fields.inventoryHelp')}
            </Text>
          }
          style={{ marginBottom: isEditing ? 12 : 0 }}
        >
          <Row gutter={[12, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={t('productModal.fields.minimumInventory')}
                required
              >
                <AInput
                  placeholder={t(
                    'productModal.fields.minimumInventoryPlaceholder',
                  )}
                  name="minimum_inventory"
                  type="number"
                  min={0}
                  value={minimumInventory}
                  onChange={(e) => setMinimumInventory(+e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12}>
              <Form.Item
                label={t('productModal.fields.maximumInventory')}
                required
              >
                <AInput
                  placeholder={t(
                    'productModal.fields.maximumInventoryPlaceholder',
                  )}
                  name="maximum_inventory"
                  type="number"
                  min={0}
                  value={maximumInventory}
                  onChange={(e) => setMaximumInventory(+e.target.value)}
                />
              </Form.Item>
            </Col>

            {minimumInventory > 0 && maximumInventory > 0 && (
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('productModal.fields.inventoryRange', {
                    min: minimumInventory,
                    max: maximumInventory,
                    unity: unity ? ` ${unity}` : '',
                  })}
                </Text>
              </Col>
            )}
          </Row>
        </Card>

        {isEditing && (
          <Card size="small">
            <Form.Item
              label={t('productModal.fields.productStatus')}
              style={{ marginBottom: 0 }}
            >
              <Space align="center" size={10}>
                <ASwitch
                  checked={status}
                  checkedChildren={t('common.yes')}
                  unCheckedChildren={t('common.no')}
                  onChange={() => {
                    setStatus(!status);
                  }}
                />
                <Text type="secondary">
                  {status
                    ? t('productModal.fields.activeDescription')
                    : t('productModal.fields.inactiveDescription')}
                </Text>
              </Space>
            </Form.Item>
          </Card>
        )}
      </Form>
    </AModal>
  );
};

export default ProductCreateEdit;
